import dayjs from 'dayjs';

/**
 * Single choke point for date and time DISPLAY formatting. The current formats
 * live in a module-level store (rather than React context alone) so non-React
 * code — e.g. the display-template formatters in src/utils/formatters.js — can
 * read the user's preferences. PreferencesContext calls setDateFormat() and
 * setTimeFormat() when the preferences load or change.
 *
 * The two preferences are stored differently on purpose. `dateFormat` IS a
 * dayjs pattern (the user picks the ordering they read dates in), while
 * `timeFormat` is the abstract choice '12h' | '24h' — a 24-hour clock is one
 * fact about a person, not a pattern they should have to spell, and the same
 * choice has to drive controls (the TimePicker in the reminder editor) that
 * take a format string of their own.
 */

export const DEFAULT_DATE_FORMAT = 'MMM D, YYYY';

export const DEFAULT_TIME_FORMAT = '12h';

/** The one place '12h'/'24h' becomes a dayjs pattern. */
export const TIME_FORMAT_PATTERNS = { '12h': 'h:mm A', '24h': 'HH:mm' };

let currentDateFormat = DEFAULT_DATE_FORMAT;
let currentTimeFormat = DEFAULT_TIME_FORMAT;

const listeners = new Set();

const notify = () => listeners.forEach(listener => listener());

// Components that render dates subscribe via src/hooks/useDateFormat.js so a
// preference change re-renders them live; non-React callers just read.
export function subscribeDateFormat(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/** The same subscription; times and dates change through one store. */
export const subscribeTimeFormat = subscribeDateFormat;

export function setDateFormat(fmt) {
	const next = fmt || DEFAULT_DATE_FORMAT;
	if (next === currentDateFormat) return;
	currentDateFormat = next;
	notify();
}

export function getDateFormat() {
	return currentDateFormat;
}

/** The preference itself ('12h' | '24h'), for controls that need the choice. */
export function setTimeFormat(pref) {
	const next = TIME_FORMAT_PATTERNS[pref] ? pref : DEFAULT_TIME_FORMAT;
	if (next === currentTimeFormat) return;
	currentTimeFormat = next;
	notify();
}

export function getTimeFormat() {
	return currentTimeFormat;
}

/** The dayjs pattern the preference means. */
export function getTimePattern() {
	return TIME_FORMAT_PATTERNS[currentTimeFormat] || TIME_FORMAT_PATTERNS[DEFAULT_TIME_FORMAT];
}

export function getDateTimeFormat() {
	return currentDateFormat + ' ' + getTimePattern();
}

export function formatDisplayDate(value) {
	if (!value) return '';
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format(getDateFormat()) : String(value);
}

export function formatDisplayDateTime(value) {
	if (!value) return '';
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format(getDateTimeFormat()) : String(value);
}

/**
 * An item's `source.createdAt` — the date the record was made in the system
 * it came from: a calendar date (YYYY-MM-DD) as a date, never shifted by a
 * zone; a date-time (stored in UTC) with its time.
 */
export function formatSourceDate(value) {
	if (!value) return '';
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
	if (!m) return formatDisplayDateTime(value);
	// Built from its own digits: dayjs('0050-03-15') goes through
	// new Date(y, m, d), which reads a year below 100 as 19xx — and a source
	// date has no lower bound.
	return formatDisplayDate(dayjs(new Date(2000, 0, 1)).year(Number(m[1])).month(Number(m[2]) - 1).date(Number(m[3])));
}

/**
 * A short date for titles and meta lines: "Sep 9" this year, "Sep 9, 2025"
 * for another year. Not preference-driven on purpose — it is a label, not a
 * reading, and it has to stay short.
 */
export function formatShortDate(value) {
	if (!value) return '';
	const day = dayjs(value);
	if (!day.isValid()) return '';
	return day.year() === dayjs().year() ? day.format('MMM D') : day.format('MMM D, YYYY');
}

/** formatShortDate plus the time, in the viewer's time pattern: "Sep 9, 3:15 PM". */
export function formatShortDateTime(value) {
	if (!value) return '';
	const day = dayjs(value);
	if (!day.isValid()) return '';
	return `${formatShortDate(value)}, ${day.format(getTimePattern())}`;
}

/* -------------------------------------------------------------------------
 * Stored shapes
 *
 * A `date` element stores the CALENDAR DAY the user picked — 'YYYY-MM-DD',
 * never a timestamp. The API slices a stored timestamp's UTC day off, so a
 * timestamp made local midnight east of UTC ("2026-09-13T00:00+10:00" =
 * "2026-09-12T14:00Z") landed on the wrong day for everyone but the
 * Americas. A `time` stores 'HH:mm' on a 24-hour clock, the shape the API's
 * own paths already write. A `date-time` keeps the full ISO timestamp: it
 * names an instant, and an instant needs its offset.
 *
 * Both LEGACY shapes are still read here, because items written by the old
 * field are still in the database: an ISO timestamp under a `date` reads as
 * its LOCAL calendar day (what the old picker showed the person who typed
 * it) and an ISO timestamp under a `time` reads as its local clock time.
 * ------------------------------------------------------------------------- */

export const STORED_DATE_FORMAT = 'YYYY-MM-DD';

export const STORED_TIME_FORMAT = 'HH:mm';

const BARE_TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/** A dayjs (or anything dayjs reads) as the day a `date` element stores. */
export function toStoredDate(value) {
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.format(STORED_DATE_FORMAT) : null;
}

/** A dayjs (or anything dayjs reads) as the clock time a `time` element stores. */
export function toStoredTime(value) {
	const parsed = parseStoredTime(value);
	return parsed ? parsed.format(STORED_TIME_FORMAT) : null;
}

/**
 * A dayjs (or anything dayjs reads) as the instant a `date-time` element
 * stores — the WHOLE instant, seconds and all. Typed entry lands on :00 of
 * its own accord (the grammar builds a day and a clock), but a value that
 * names a moment — "Now", `defaultToNow`, a stepped or stepped-from reading —
 * keeps the seconds it was taken at, so two readings a few seconds apart
 * stay two readings and still sort against each other.
 */
export function toStoredDateTime(value) {
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.toISOString() : null;
}

// The two COARSE spellings a `date` element's value may arrive in. The web
// always commits a whole day (the first of the month, or Jan 1) whatever the
// element's `precision` is — a value is a day, and only its READING is
// coarse — but the API accepts 'YYYY-MM' and 'YYYY' and expands them, so
// anything written by an import, an automation or a direct API call can be
// one of these. dayjs reads '2026-09' but NOT a bare '2026', so both are
// spelled out here rather than left to it.
const YEAR_ONLY_RE = /^\d{4}$/;
const YEAR_MONTH_RE = /^\d{4}-\d{2}$/;

/**
 * A stored `date` as a dayjs at local midnight. 'YYYY-MM-DD' parses as the
 * local day already; a legacy ISO timestamp is read in the viewer's zone and
 * truncated, which is the day the old picker displayed; a coarse 'YYYY-MM' or
 * 'YYYY' is the first day it names.
 */
export function parseStoredDate(value) {
	if (value === null || value === undefined || value === '') return null;
	if (typeof value === 'string') {
		const text = value.trim();
		if (YEAR_ONLY_RE.test(text)) return dayjs(`${text}-01-01`).startOf('day');
		if (YEAR_MONTH_RE.test(text)) return dayjs(`${text}-01`).startOf('day');
	}
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed.startOf('day') : null;
}

/* -------------------------------------------------------------------------
 * Precision
 *
 * `options.precision` ('day' | 'month' | 'year') is DISPLAY AND ENTRY only:
 * a `date` element always stores a whole 'YYYY-MM-DD' (the first of the
 * month, or Jan 1), so sorting, KQL, the primary date, formulas and exports
 * are untouched by it — exactly the reasoning that keeps a range two plain
 * elements. What changes is what the reader is shown and what the calendar
 * offers: "September 2026", "2026".
 * ------------------------------------------------------------------------- */

export const DATE_PRECISIONS = ['day', 'month', 'year'];

const PRECISION_PATTERNS = { month: 'MMMM YYYY', year: 'YYYY' };

const SHORT_PRECISION_PATTERNS = { month: 'MMM YYYY', year: 'YYYY' };

/**
 * A stored day in the words its element's precision reads it in. 'day' is the
 * ordinary preference-driven date, so every caller can pass the precision
 * blind and a plain date element is unchanged.
 */
export function formatDateByPrecision(value, precision = 'day') {
	if (value === null || value === undefined || value === '') return '';
	const pattern = PRECISION_PATTERNS[precision];
	if (!pattern) return formatDisplayDate(value);
	const parsed = parseStoredDate(value);
	return parsed && parsed.isValid() ? parsed.format(pattern) : String(value);
}

/** The same, in the SHORT label spelling tiles and headers use. */
export function formatShortDateByPrecision(value, precision = 'day') {
	if (value === null || value === undefined || value === '') return '';
	const pattern = SHORT_PRECISION_PATTERNS[precision];
	if (!pattern) return formatShortDate(value);
	const parsed = parseStoredDate(value);
	return parsed && parsed.isValid() ? parsed.format(pattern) : '';
}

/**
 * ONLY the bare clock shape ('HH:mm', or 'HH:mm:ss'), on `reference`'s day —
 * null for anything else, including a perfectly good date string. Callers
 * that want to treat a clock differently from a timestamp (the display
 * template's formatters anchor one to the epoch day and read the other where
 * it stands) need to be able to tell them apart.
 */
export function parseBareTime(value, reference) {
	if (typeof value !== 'string') return null;
	const match = BARE_TIME_RE.exec(value.trim());
	if (!match) return null;
	const hour = Number(match[1]);
	const minute = Number(match[2]);
	if (hour > 23 || minute > 59) return null;
	return (reference ? dayjs(reference) : dayjs()).hour(hour).minute(minute).second(0).millisecond(0);
}

/**
 * A stored `time` as a dayjs on `reference`'s day (today by default). 'HH:mm'
 * is not a shape dayjs reads on its own, so it is matched here; a legacy ISO
 * timestamp contributes its local clock time.
 */
export function parseStoredTime(value, reference) {
	if (value === null || value === undefined || value === '') return null;
	const bare = parseBareTime(value, reference);
	if (bare) return bare;
	const base = reference ? dayjs(reference) : dayjs();
	const parsed = dayjs(value);
	if (!parsed.isValid()) return null;
	return base.hour(parsed.hour()).minute(parsed.minute()).second(0).millisecond(0);
}

/** A stored `time` ('HH:mm', or a legacy ISO timestamp) in the clock preference. */
export function formatDisplayTime(value) {
	if (value === null || value === undefined || value === '') return '';
	const parsed = parseStoredTime(value);
	return parsed ? parsed.format(getTimePattern()) : String(value);
}

/* -------------------------------------------------------------------------
 * Spans
 *
 * A range is two plain elements (utils/dateBounds), so every surface that
 * reads one needs the same sentence out of the two values. This is that one
 * sentence: "Sep 9 – Sep 12 · 3 days", "Sep 9, 3:00 PM – 5:15 PM · 2h 15m",
 * "Sep 9" when both land on one day, "Sep 9 →" while the end is still open.
 * ------------------------------------------------------------------------- */

const EN_DASH = '–';

/** A span's length in the coarsest words that stay true: "3 days", "2h 15m". */
export function formatDuration(from, to, dataType = 'date') {
	if (!from || !to) return '';
	const minutes = to.diff(from, 'minute');
	if (minutes < 0) return '';
	if (dataType === 'date') {
		const days = to.startOf('day').diff(from.startOf('day'), 'day');
		if (days <= 0) return '';
		return `${days} day${days === 1 ? '' : 's'}`;
	}
	if (minutes === 0) return '';
	const days = Math.floor(minutes / 1440);
	const hours = Math.floor((minutes % 1440) / 60);
	const mins = minutes % 60;
	if (days) return `${days}d${hours ? ` ${hours}h` : ''}`;
	if (hours) return `${hours}h${mins ? ` ${mins}m` : ''}`;
	return `${mins}m`;
}

/**
 * The two stored values of a range as one line. `dataType` is the pair's own
 * type ('date' | 'date-time' | 'time'); both empty is an empty span, and an
 * empty end is an OPEN one — "Sep 9 →" says the record has a start and no
 * finish, which is a fact worth reading rather than a blank. An empty START
 * with an end is its mirror, "→ Sep 12" (KPR-626: the tile and the read view
 * draw the pair as one value, so the end's date must not vanish; the owner has
 * not confirmed this spelling — k03 — and it is this one line to change).
 */
export function formatDateRange(startValue, endValue, dataType = 'date') {
	const empty = value => value === null || value === undefined || value === '';
	if (empty(startValue)) {
		if (empty(endValue)) return '';
		const end = dataType === 'time' ? parseStoredTime(endValue) : dayjs(endValue);
		if (!end || !end.isValid()) return '';
		const endText = dataType === 'time'
			? end.format(getTimePattern())
			: (dataType === 'date-time' ? formatShortDateTime(endValue) : formatShortDate(endValue));
		return `→ ${endText}`;
	}
	const clock = getTimePattern();
	const start = dataType === 'time' ? parseStoredTime(startValue) : dayjs(startValue);
	if (!start || !start.isValid()) return String(startValue);

	const startText = dataType === 'time'
		? start.format(clock)
		: (dataType === 'date-time' ? formatShortDateTime(startValue) : formatShortDate(startValue));

	if (endValue === null || endValue === undefined || endValue === '') return `${startText} →`;
	const end = dataType === 'time' ? parseStoredTime(endValue, start) : dayjs(endValue);
	if (!end || !end.isValid()) return startText;

	const sameDay = start.isSame(end, 'day');
	if (dataType === 'date') {
		if (sameDay) return startText;
		return `${startText} ${EN_DASH} ${formatShortDate(endValue)} · ${formatDuration(start, end, 'date')}`.replace(/ · $/, '');
	}

	// A date-time that ends on the day it started repeats no date: the second
	// half of "Sep 9, 3:00 PM – 5:15 PM" is a clock, not another day.
	const endText = dataType === 'time'
		? end.format(clock)
		: (sameDay ? end.format(clock) : formatShortDateTime(endValue));
	const duration = formatDuration(start, end, dataType);
	return `${startText} ${EN_DASH} ${endText}${duration ? ` · ${duration}` : ''}`;
}
